import React from 'react'

import { fromUnixTime } from 'date-fns/fromUnixTime'
import { getUnixTime } from 'date-fns/getUnixTime'

import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'

import { Cancel, CheckCircle, Icon } from '@mxenabled/mx-icons'
import { MerchantLogo, Text } from '@mxenabled/mxui'

import {
  ANALYTICS_EVENTS,
  formatCurrency,
  ListItemWrapper,
  useEvent,
  useGlobalCopyStore,
  useRecurringTransactionsStore,
} from '../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'

import {
  type Recurrence,
  type Transaction,
  RecurrenceStatus,
  RepeatingTransaction,
} from '../../core'

type RecurringTransactionRow = {
  key: string
  name: string
  amount: number
  date: number
  merchantGuid: string
  categoryGuid: string
  category: string
  status: RecurrenceStatus.Paid | RecurrenceStatus.Missed
  transactionGuid?: string
}

const buildRows = (
  transactions: Transaction[],
  missedRecurrences: Recurrence[],
): RecurringTransactionRow[] => {
  const paidRows: RecurringTransactionRow[] = transactions.map((t) => ({
    key: t.guid,
    name: t.payee || t.description || t.feed_description,
    amount: t.amount,
    date: t.date,
    merchantGuid: t.merchant_guid || '',
    categoryGuid: t.top_level_category_guid || '',
    category: t.category || '',
    status: RecurrenceStatus.Paid,
    transactionGuid: t.guid,
  }))

  const missedRows: RecurringTransactionRow[] = missedRecurrences.map((r) => ({
    key: `missed-${r.expectedDate.getTime()}`,
    name: r.name,
    amount: r.amount,
    date: getUnixTime(r.expectedDate),
    merchantGuid: r.merchantGuid || '',
    categoryGuid: r.categoryGuid,
    category: '',
    status: RecurrenceStatus.Missed,
  }))

  return [...paidRows, ...missedRows].sort((a, b) => b.date - a.date)
}

interface RecurringTransactionListProps {
  isIncome?: boolean
  onMarkAsPaidClick: () => void
  onMarkAsUnpaidClick: (transactionGuid: string) => void
  onTransactionClick: (guid: string) => void
  recurringTransaction: RepeatingTransaction
  transactions: Transaction[]
}

export const RecurringTransactionList = ({
  isIncome = false,
  onMarkAsUnpaidClick,
  onMarkAsPaidClick,
  onTransactionClick,
  recurringTransaction,
  transactions,
}: RecurringTransactionListProps) => {
  const { recurring: copy } = useGlobalCopyStore()
  const { onEvent } = useEvent()
  const { recurrences } = useRecurringTransactionsStore()

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(null)
  const [menuRow, setMenuRow] = React.useState<RecurringTransactionRow | null>(null)

  const missedRecurrences = React.useMemo(
    () =>
      recurrences.filter(
        (r) =>
          r.repeatingTransactionGuid === recurringTransaction.guid &&
          r.status === RecurrenceStatus.Missed,
      ),
    [recurrences, recurringTransaction.guid],
  )

  const rows = React.useMemo(
    () => buildRows(transactions, missedRecurrences),
    [transactions, missedRecurrences],
  )

  const handleClick = (row: RecurringTransactionRow) => {
    if (row.transactionGuid) {
      onTransactionClick(row.transactionGuid)
    }
  }

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, row: RecurringTransactionRow) => {
    e.stopPropagation()
    setMenuAnchorEl(e.currentTarget)
    setMenuRow(row)
    onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_LIST_CLICK_MORE_MENU)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setMenuRow(null)
  }

  const handleMenuAction = () => {
    if (!menuRow) return

    if (menuRow.status === RecurrenceStatus.Missed) {
      onMarkAsPaidClick()
    } else if (menuRow.transactionGuid) {
      onMarkAsUnpaidClick(menuRow.transactionGuid)
    }
    handleMenuClose()
  }

  return (
    <Stack direction="column" sx={{ minHeight: '30dvh' }}>
      <Text bold={true} id="past-activity" sx={{ ml: 24, mt: 16, mb: 8 }} variant="body1">
        {copy.past_activity}
      </Text>
      <List aria-labelledby="past-activity" sx={{ flex: 1, overflow: 'auto' }}>
        {rows.map((row, index) => {
          const isPaid = row.status === RecurrenceStatus.Paid
          const isMissed = row.status === RecurrenceStatus.Missed
          const isLast = index === rows.length - 1

          return (
            <React.Fragment key={row.key}>
              <ListItemWrapper
                onClick={isPaid ? () => handleClick(row) : undefined}
                sx={{ '& .MuiListItemButton-root': { pr: 10 } }}
              >
                <ListItemAvatar>
                  <MerchantLogo categoryGuid={row.categoryGuid} merchantGuid={row.merchantGuid} />
                </ListItemAvatar>
                <ListItemText
                  disableTypography={true}
                  secondary={
                    <Stack direction="row" justifyContent="space-between">
                      <Text variant="caption">
                        {formatDate(fromUnixTime(row.date), DATE_FORMATS_INTL.MONTH_DAY_YEAR)}
                      </Text>
                      <Stack
                        alignItems="center"
                        direction="row"
                        gap={6}
                        sx={{ textTransform: 'uppercase' }}
                      >
                        {isPaid && <CheckCircle color="success" filled={true} size={12} />}
                        {isMissed && <Cancel color="error" filled={true} size={12} />}
                        <Text bold={true} color="text.secondary" variant="caption">
                          {isPaid ? copy.paid?.toLowerCase() : copy.missed?.toLowerCase()}
                        </Text>
                      </Stack>
                    </Stack>
                  }
                >
                  <Stack direction="row" justifyContent="space-between" sx={{ gap: 8 }}>
                    <Text bold={true} variant="body1">
                      {row.name}
                    </Text>
                    <Text
                      bold={true}
                      color={isIncome ? 'success.main' : 'text.primary'}
                      sx={{ flexShrink: 0 }}
                      variant="body1"
                    >
                      {isIncome ? '+' : ''}
                      {formatCurrency(row.amount, '0,0.00')}
                    </Text>
                  </Stack>
                </ListItemText>
                <IconButton
                  aria-label={copy.more_menu_aria}
                  onClick={(e) => handleMenuOpen(e, row)}
                  size="small"
                  sx={{ alignSelf: 'center', ml: 4 }}
                >
                  <Icon name="more_vert" size={20} sx={{ color: 'text.secondary' }} />
                </IconButton>
              </ListItemWrapper>
              <Divider variant={isLast ? 'fullWidth' : 'inset'} />
            </React.Fragment>
          )
        })}
      </List>

      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        disableScrollLock={true}
        onClose={handleMenuClose}
        open={Boolean(menuAnchorEl)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem onClick={handleMenuAction} sx={{ p: 12 }}>
          <Text variant="body1">
            {menuRow?.status === RecurrenceStatus.Missed
              ? copy.mark_as_paid_cta
              : copy.mark_as_unpaid_cta}
          </Text>
        </MenuItem>
      </Menu>
    </Stack>
  )
}
